import React, { FC, ReactElement, useCallback, useContext, useMemo, useState } from 'react'
import Skeleton from '@material-ui/lab/Skeleton'
import { IsStringEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { DeviceSelectItem, SelectOptions } from '../../Common/Components/Selects/Types'
import { MonitoringStatuses } from '../Constants/Constants'
import Select from '../../Common/Components/Selects/Select'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { ManageQueues as ManageQueuesLanguage } from '../../../../i18n'
import { requestJSON } from '../../../NonReact/common/API/REST/RESTClient'
import { deviceURL } from '../../../NonReact/constants/ApiConstants'
import { RESTMethods } from '../../../NonReact/constants/REST'
import { UpdateQueueRequestBody } from '../Types/Types'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { Device, IndividualTelItem, ReactState } from '../../Common/Types/Types'
import Loader from '../../Common/Components/Loader'
import { EMPTY_STRING } from '../../Common/Constants/Separators'
import { DevicesResponse } from '../../../NonReact/Types/Types'

type Props = {
    callQueue: DeviceSelectItem
    queueError: Definable<string>
    currentTelSys: Nullable<IndividualTelItem>
    telsys: number
    partition: number
    fetchDevicesFromPartition: (
        telsysValue: number,
        partitionValue: number,
        setLoading?: (loading: boolean) => void
    ) => void
}

export const ManageQueueEntry: FC<Props> = ({
    callQueue: { original: originalDevice, additionalDevices },
    queueError,
    currentTelSys,
    telsys,
    partition,
    fetchDevicesFromPartition
}: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(ManageQueuesLanguage.NAME)

    const { displayError }: MessageContextType = useContext(MessageContext)

    const [isDataLoading, setIsDataLoading]: ReactState<boolean> = useState<boolean>(false)

    const allOriginalDevices: DevicesResponse[] = useMemo(
        (): DevicesResponse[] =>
            [
                ...(originalDevice ? [originalDevice] : []),
                ...(additionalDevices?.map(({ original }: DeviceSelectItem): Nullable<DevicesResponse> => original) ??
                    [])
            ].filter(isVarNotEmpty),
        [originalDevice, JSON.stringify(additionalDevices)]
    )

    const allQueueStatuses: string[] = useMemo(
        (): string[] =>
            allOriginalDevices.map(({ QueueStatus }: DevicesResponse): string => QueueStatus ?? EMPTY_STRING),
        [JSON.stringify(allOriginalDevices)]
    )

    const updateAllQueues = useCallback(
        (devicesToUpdate: DevicesResponse[], realTime: boolean): Promise<unknown> =>
            Promise.all(
                devicesToUpdate.map(
                    (device: DevicesResponse): Promise<unknown> =>
                        requestJSON(deviceURL(`${telsys}`, `${partition}`, `${device?.DeviceNumber}`), {
                            method: RESTMethods.PUT,
                            body: {
                                DisableRTMonitoring: realTime
                            } as UpdateQueueRequestBody
                        })
                )
            ),
        [telsys, partition]
    )

    const updateQueue = useCallback(
        (realTime: boolean): void => {
            setIsDataLoading(true)
            updateAllQueues(
                [
                    ...(originalDevice ? [originalDevice] : []),
                    ...(additionalDevices
                        ?.map(
                            (additionalDevice: DeviceSelectItem): Nullable<DevicesResponse> =>
                                additionalDevice?.original
                        )
                        ?.filter(isVarNotEmpty) ?? [])
                ],
                realTime
            )
                .then((): void => {
                    setTimeout((): void => {
                        fetchDevicesFromPartition(telsys, partition, setIsDataLoading)
                    }, 300)
                })
                .catch((error: Error): void => {
                    displayError(error)
                    setIsDataLoading(false)
                })
        },
        [telsys, partition, originalDevice?.DeviceNumber]
    )

    const isQueueLoading: boolean = useMemo(
        (): boolean =>
            allQueueStatuses.some(
                (status: string): boolean =>
                    !Object.values(MonitoringStatuses).includes(status) && status !== EMPTY_STRING
            ),
        [allQueueStatuses]
    )

    const isQueueUnknown: boolean = useMemo(
        (): boolean => allQueueStatuses.some((status: string): boolean => status === EMPTY_STRING),
        [allQueueStatuses]
    )

    const isQueuePending: boolean = useMemo(
        (): boolean =>
            allQueueStatuses.some((status: string): boolean =>
                [MonitoringStatuses.ENABLING_MONITORING, MonitoringStatuses.DISABLING_MONITORING].includes(status)
            ),
        [allQueueStatuses]
    )

    const isQueuePendingLanguage: string = useMemo((): string => {
        if (allQueueStatuses.some((status: string): boolean => status === MonitoringStatuses.ENABLING_MONITORING)) {
            return language.get(ManageQueuesLanguage.ManageQueue_Pending_Queue_Enabling)
        }

        if (allQueueStatuses.some((status: string): boolean => status === MonitoringStatuses.DISABLING_MONITORING)) {
            return language.get(ManageQueuesLanguage.ManageQueue_Pending_Queue_Disabling)
        }

        return EMPTY_STRING
    }, [allQueueStatuses])

    const isCompatible: boolean =
        allOriginalDevices.every((device: DevicesResponse): boolean => !!device?.QueueCompatibility) ?? true
    const isRealTimeForced: boolean = originalDevice?.QueueStatus === MonitoringStatuses.REAL_TIME_FORCED

    const monitoringStatus: SelectOptions<string> = useMemo(
        (): SelectOptions<string> => [
            {
                label: language.get(ManageQueuesLanguage.ManageQueueCallMonitoringHistoric),
                value: MonitoringStatuses.HISTORIC
            },
            isCompatible
                ? {
                      label: language.get(ManageQueuesLanguage.ManageQueueCallMonitoringRealTime),
                      value: MonitoringStatuses.REAL_TIME
                  }
                : {
                      label: language.get(ManageQueuesLanguage.ManageQueueCallMonitoringRealTimeForced),
                      value: MonitoringStatuses.REAL_TIME_FORCED,
                      className: 'mq-real-time-forced'
                  },
            ...(!isQueueUnknown
                ? []
                : [
                      {
                          label: language.get(ManageQueuesLanguage.ManageQueueCallMonitoringUnknown),
                          value: MonitoringStatuses.UNKNOWN
                      }
                  ])
        ],
        [isCompatible, isQueueUnknown]
    )

    const displayMonitoringStatus: ReactElement = useMemo((): ReactElement => {
        if (isQueueLoading) {
            return (
                <div className='queue-pending'>
                    <Loader size={25} />
                </div>
            )
        }

        if (isQueuePending) {
            return (
                <div className='queue-pending'>
                    <Loader size={25} />
                    {isQueuePendingLanguage}
                </div>
            )
        }

        const isDisabled: Nullable<boolean> =
            currentTelSys?.HistoricOnly || isQueueUnknown || currentTelSys?.BotsDisabled || !currentTelSys?.CommsEnabled

        return (
            <Select
                classNames={isRealTimeForced ? 'mq-real-time-forced' : ''}
                value={originalDevice?.QueueStatus ?? MonitoringStatuses.UNKNOWN}
                options={monitoringStatus}
                renderSelectedValue={false}
                disabled={isDisabled}
                rules={{
                    required: true
                }}
                handleChange={(value: string): void => {
                    updateQueue(value === MonitoringStatuses.HISTORIC)
                }}
            />
        )
    }, [
        isQueueLoading,
        isQueuePending,
        isQueuePendingLanguage,
        isRealTimeForced,
        monitoringStatus,
        JSON.stringify(originalDevice),
        JSON.stringify(currentTelSys)
    ])

    const deviceDisplay = (origDev?: Device): ReactElement => (
        <div className='mq-device-display' key={`device-display-${origDev?.DeviceNumber}`}>
            <div className='mq-col'>
                <span className='mq-col-label'>
                    {origDev?.DeviceNumber}&nbsp;-&nbsp;{origDev?.Description}
                </span>
            </div>

            <div className='mq-col'>
                <span className='mq-col-label'>
                    {isCompatible
                        ? language.get(ManageQueuesLanguage.ManageQueueCallCQConfigurationCompatible)
                        : language.get(ManageQueuesLanguage.ManageQueueCallCQConfigurationIncompatible)}
                </span>
            </div>

            <div className='mq-col'>
                <span className='mq-col-label'>{origDev?.TelSysUserID}</span>
            </div>
        </div>
    )

    return (
        <>
            <div className={`mq-entry ${isDataLoading ? 'hide' : ''}`}>
                <div className='mq-main-content'>
                    <div>
                        {deviceDisplay(originalDevice)}
                        {additionalDevices?.map(({ original: origDev }: DeviceSelectItem) => deviceDisplay(origDev))}
                    </div>

                    <div className='mq-col-monitoring'>
                        <div className='mq-entry-field'>{displayMonitoringStatus}</div>
                    </div>
                </div>

                {!IsStringEmpty(queueError) ? <div className='mq-error-message'>{queueError}</div> : <></>}
            </div>

            {isDataLoading ? <Skeleton /> : <></>}
        </>
    )
}
